import { Search, X } from "lucide-react";
import { ListWarning } from "../../web/components/ui";

/*
 * The top row of the Price List's lists (materials, bundles, suppliers), the same in each: search
 * (every word, anywhere: "door closer") and what to show (a dropdown, each with its count). Rows
 * that need attention are flagged in the list itself (ListWarning, is-warning).
 */

/** Whether `text` has every word of a search, in any order and case. */
export function matchesSearch(text: string, query: string) {
    const lower = text.toLowerCase();
    return query
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean)
        .every((w) => lower.includes(w));
}

export function ListToolbar<F extends string>(
    props: Readonly<{
        /** What it searches, for screen readers ("Search materials"), and the hint in the box. */
        searchLabel: string;
        placeholder: string;
        query: string;
        onQuery: (q: string) => void;
        filter: F;
        onFilter: (f: F) => void;
        /** The Show dropdown: its options, in groups (a null label: not in a group). */
        groups: readonly { label: string | null; filters: readonly F[] }[];
        /** Each option's label in the dropdown ("Active items"), and how many it shows. */
        labels: Record<F, string>;
        counts: Record<F, number>;
    }>,
) {
    const { filter, counts } = props;
    const option = (f: F) => (
        <option key={f} value={f}>
            {props.labels[f]} ({counts[f].toLocaleString()})
        </option>
    );
    return (
        <div className="flex flex-wrap items-center gap-3 border-b border-line px-5 py-3">
            <label className="relative min-w-60 flex-1">
                <span className="sr-only">{props.searchLabel}</span>
                <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted" />
                <input
                    type="text"
                    className="input pr-9 pl-9"
                    placeholder={props.placeholder}
                    value={props.query}
                    onChange={(e) => props.onQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Escape" && props.onQuery("")}
                />
                {props.query ? (
                    <button type="button" className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-1 text-muted hover:text-ink" aria-label="Clear the search" onClick={() => props.onQuery("")}>
                        <X className="size-3.5" />
                    </button>
                ) : null}
            </label>
            <label className="flex items-center gap-2">
                <span className="text-muted">Show</span>
                <select className="input w-64" value={filter} onChange={(e) => props.onFilter(e.target.value as F)}>
                    {props.groups.map((g) =>
                        g.label ? (
                            <optgroup key={g.label} label={g.label}>
                                {g.filters.map(option)}
                            </optgroup>
                        ) : (
                            g.filters.map(option)
                        ),
                    )}
                </select>
            </label>
        </div>
    );
}

/**
 * What needs attention on a row, as one mark after its name (a count when more than one; hover lists
 * them, worst first): the same in every list. Nothing when there's nothing.
 */
export function NeedsAttention({ problems, danger = false }: Readonly<{ problems: readonly string[]; danger?: boolean }>) {
    if (!problems.length) return null;
    return (
        <ListWarning tone={danger ? "danger" : "warning"} title={`Needs attention:\n${problems.map((x) => `• ${x}`).join("\n")}\nOpen it to see more.`}>
            {problems.length > 1 ? problems.length.toLocaleString() : undefined}
        </ListWarning>
    );
}
